// API service - handles all backend communication
// Implements US-1.2: Room join API calls and US-2.1: Card CRUD operations

import { Room, RoomJoinResponse } from '../../../shared/types';
import { Card } from '../types/card';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * GET authentication token from localStorage
 * Used to add Authorization header to authenticated requests
 */
function getAuthToken(): string | null {
  return localStorage.getItem('elegy_auth_token');
}

/**
 * CREATE headers for authenticated requests
 * Adds Authorization Bearer token if available
 */
function createAuthHeaders(additionalHeaders: Record<string, string> = {}): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * HANDLE fetch response and check for auth errors
 * Redirects to login if token is expired
 */
async function handleResponse(response: Response): Promise<any> {
  // TOKEN expired - clear localStorage and redirect to home
  if (response.status === 401) {
    console.warn('Token expired, logging out...');
    localStorage.removeItem('elegy_auth_token');
    window.location.href = '/';
    throw new Error('Session expired');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Request failed');
  }

  return data;
}

/**
 * JOIN room as participant
 * POST /api/rooms/join
 * Includes auth token if available (for facilitator joining own room)
 */
export async function joinRoom(
  code: string,
  displayName?: string
): Promise<RoomJoinResponse> {
  const response = await fetch(`${API_BASE_URL}/rooms/join`, {
    method: 'POST',
    headers: createAuthHeaders(),
    body: JSON.stringify({
      code,
      display_name: displayName,
    }),
  });

  const data = await handleResponse(response);

  if (!data.success) {
    throw new Error(data.error || 'Failed to join room');
  }

  return data.data;
}

/**
 * GET room by ID
 * GET /api/rooms/:id
 */
export async function getRoomById(roomId: string): Promise<Room> {
  const response = await fetch(`${API_BASE_URL}/rooms/${roomId}`);

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to fetch room');
  }

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch room');
  }

  return data.data.room;
}

/**
 * FETCH all cards for a room
 * GET /api/rooms/:roomId/cards
 */
export async function fetchCards(roomId: string, sessionId: string): Promise<Card[]> {
  const response = await fetch(`${API_BASE_URL}/rooms/${roomId}/cards`, {
    headers: {
      'X-Session-ID': sessionId,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch cards');
  }

  return data;
}

/**
 * CREATE new card
 * POST /api/rooms/:roomId/cards
 */
export async function createCard(
  roomId: string,
  sessionId: string,
  columnKey: string,
  text: string,
  authorName?: string
): Promise<Card> {
  const response = await fetch(`${API_BASE_URL}/rooms/${roomId}/cards`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-ID': sessionId,
    },
    body: JSON.stringify({
      columnKey,
      text,
      authorName,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to create card');
  }

  return data;
}

/**
 * UPDATE card text
 * PATCH /api/cards/:cardId
 */
export async function updateCard(
  cardId: string,
  sessionId: string,
  text: string
): Promise<Card> {
  const response = await fetch(`${API_BASE_URL}/cards/${cardId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-ID': sessionId,
    },
    body: JSON.stringify({ text }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to update card');
  }

  return data;
}

/**
 * DELETE card
 * DELETE /api/cards/:cardId
 */
export async function deleteCard(cardId: string, sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/cards/${cardId}`, {
    method: 'DELETE',
    headers: {
      'X-Session-ID': sessionId,
    },
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to delete card');
  }
}

/**
 * CREATE new room (facilitator only - requires authentication)
 * POST /api/rooms
 * NOTE: facilitator_id comes from JWT token, not request body
 */
export async function createRoom(params: {
  template: string;
  name?: string;
  anonymousMode: boolean;
  votingMode: 'dot_voting' | 'none';
}): Promise<{ room: Room }> {
  const response = await fetch(`${API_BASE_URL}/rooms`, {
    method: 'POST',
    headers: createAuthHeaders(),
    body: JSON.stringify({
      template: params.template,
      name: params.name,
      anonymous_mode: params.anonymousMode,
      voting_mode: params.votingMode,
    }),
  });

  const data = await handleResponse(response);

  if (!data.success) {
    throw new Error(data.error || 'Failed to create room');
  }

  return data.data;
}

/**
 * START facilitation phase (facilitator only - requires authentication)
 * POST /api/rooms/:id/start
 */
export async function startFacilitation(roomId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/rooms/${roomId}/start`, {
    method: 'POST',
    headers: createAuthHeaders(),
  });

  const data = await handleResponse(response);

  if (!data.success) {
    throw new Error(data.error || 'Failed to start facilitation');
  }
}

/**
 * GET user's role in room (facilitator or participant)
 * GET /api/rooms/:roomId/role
 */
export async function getRoomRole(roomId: string): Promise<'facilitator' | 'participant'> {
  const response = await fetch(`${API_BASE_URL}/rooms/${roomId}/role`, {
    headers: createAuthHeaders(),
  });

  const data = await handleResponse(response);

  if (!data.success) {
    throw new Error(data.error || 'Failed to get room role');
  }

  return data.data.role;
}

// ========== VOTING API METHODS (US-3.2) ==========

/**
 * GET vote allocation for session
 * GET /api/voting/:roomId/allocation?sessionId=xxx
 */
export async function getVoteAllocation(roomId: string, sessionId: string) {
  const response = await fetch(
    `${API_BASE_URL}/voting/${roomId}/allocation?sessionId=${encodeURIComponent(sessionId)}`
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to get vote allocation');
  }

  return data;
}

// ========== EXPORT API METHODS (US-4.1) ==========

/**
 * MARK export as generated (facilitator only - requires authentication)
 * POST /api/rooms/:roomId/export/mark
 */
export async function markExportGenerated(roomId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/rooms/${roomId}/export/mark`, {
    method: 'POST',
    headers: createAuthHeaders(),
  });

  const data = await handleResponse(response);

  if (!data.success) {
    throw new Error(data.error || 'Failed to mark export as generated');
  }
}
